import { useEffect, useState } from "preact/hooks";
import type { Article, Highlight } from "../lib/types";
import { listAllHighlights, deleteHighlight } from "../lib/db";
import { navigate } from "../lib/router";
import { formatAge } from "../lib/format";

type HighlightWithArticle = Highlight & { article: Article };

const COLORS: Record<string, string> = {
  yellow: "var(--highlight-yellow)",
  green: "var(--highlight-green)",
  blue: "var(--highlight-blue)",
  pink: "var(--highlight-pink)",
};

export function Highlights() {
  const [items, setItems] = useState<HighlightWithArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        setItems(await listAllHighlights());
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const remove = async (id: string) => {
    await deleteHighlight(id);
    setItems((p) => p.filter((h) => h.id !== id));
  };

  return (
    <>
      <div class="topbar"><h1>Highlights</h1></div>
      {error && <div class="banner error">{error}</div>}
      {loading ? (
        <div class="empty"><div class="spinner" /></div>
      ) : items.length === 0 ? (
        <div class="empty">
          <h2>No highlights yet</h2>
          <p class="muted">Select text in any article to highlight it.</p>
        </div>
      ) : (
        <div class="col">
          {items.map((h) => (
            <div class="card" key={h.id} style="margin: 0;">
              <div class="row" style="margin-bottom: var(--space-2);">
                <span style={`display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${COLORS[h.color] ?? "var(--fg-muted)"};`} />
                <a href={`#/articles/${h.article_id}`} style="text-decoration: none;"
                   onClick={(e) => { e.preventDefault(); navigate(`/articles/${h.article_id}`); }}>
                  <strong class="muted">{h.article.title ?? h.article.url}</strong>
                </a>
                <div class="grow" />
                <span class="subtle" style="font-size: var(--fs-12);">{formatAge(h.created_at)}</span>
                <button class="icon ghost" onClick={() => remove(h.id)} aria-label="Delete highlight">×</button>
              </div>
              <blockquote class="reader" style="margin: 0; padding: 0; border: 0;">
                {h.exact_text}
              </blockquote>
              {h.note && <p class="muted" style="margin-top: var(--space-3); font-size: var(--fs-14);">{h.note}</p>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
